import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { TrendingDown, Package, Zap, RotateCw, AlertTriangle, HelpCircle, CheckCircle2, Clock } from "lucide-react";

const CONFIDENCE_TONE = {
  high:         { label: "High",   cls: "bg-emerald-100 text-emerald-700" },
  medium:       { label: "Medium", cls: "bg-amber-100 text-amber-700" },
  low:          { label: "Low",    cls: "bg-slate-200 text-slate-700" },
  insufficient: { label: "Insufficient", cls: "bg-slate-100 text-slate-500" },
};

const STATUS_TONE = {
  ok:                    { label: "Healthy",           cls: "bg-emerald-100 text-emerald-700" },
  order_recommended:     { label: "Order recommended", cls: "bg-amber-100 text-amber-700" },
  stockout:              { label: "Stockout",          cls: "bg-red-100 text-red-700" },
  insufficient_history:  { label: "No history",        cls: "bg-slate-100 text-slate-500" },
};

export default function AdminInventoryForecast() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/inventory/forecast"); setRows(r.data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reorder = async (row) => {
    setReordering(row.material_id);
    try {
      const r = await api.post("/inventory/reorder", { material_id: row.material_id });
      toast.success(`Draft PO ${r.data.po_number} created`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reorder failed"); }
    finally { setReordering(null); }
  };

  const needsAction     = rows.filter((r) => r.status === "order_recommended" || r.status === "stockout");
  const healthy         = rows.filter((r) => r.status === "ok");
  const insufficient    = rows.filter((r) => r.status === "insufficient_history");

  return (
    <div data-testid="admin-inventory-forecast" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Inventory</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
            Smart Demand Forecast
          </h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            Reorder points blend consumption, current stock, reservations, incoming POs, lead time, and safety stock.
          </p>
        </div>
        <button onClick={load} className="btn-outline-navy text-xs inline-flex items-center gap-1" data-testid="refresh-forecast">
          <RotateCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#94A3B8]">Computing forecast…</div>
      ) : (
        <>
          {/* Reorder needed */}
          {needsAction.length > 0 && (
            <div className="card-pb p-5 border-l-4 border-amber-500">
              <div className="inline-flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <p className="overline text-amber-600">Action required ({needsAction.length})</p>
              </div>
              <div className="space-y-3">
                {needsAction.map((r) => <ForecastRow key={r.material_id} row={r} reorder={reorder} reordering={reordering} />)}
              </div>
            </div>
          )}

          {/* Healthy */}
          <div className="card-pb p-5">
            <div className="inline-flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <p className="overline">Stock healthy ({healthy.length})</p>
            </div>
            {healthy.length === 0 ? (
              <p className="text-xs text-[#94A3B8] italic">Nothing in this bucket yet.</p>
            ) : (
              <div className="space-y-3">
                {healthy.map((r) => <ForecastRow key={r.material_id} row={r} reorder={reorder} reordering={reordering} />)}
              </div>
            )}
          </div>

          {/* Insufficient history */}
          {insufficient.length > 0 && (
            <div className="card-pb p-5">
              <div className="inline-flex items-center gap-2 mb-3">
                <HelpCircle className="w-4 h-4 text-[#94A3B8]" />
                <p className="overline text-[#64748B]">Insufficient demand history ({insufficient.length})</p>
                <span className="text-[11px] text-[#94A3B8]">— we won't show burn/coverage until at least 5 units and 3 demand events land in the last 90 days.</span>
              </div>
              <div className="space-y-3">
                {insufficient.map((r) => <ForecastRow key={r.material_id} row={r} reorder={reorder} reordering={reordering} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Pill({ tone }) {
  if (!tone) return null;
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${tone.cls}`}>{tone.label}</span>;
}

function ForecastRow({ row, reorder, reordering }) {
  const disabled = reordering === row.material_id;
  const insufficient = row.status === "insufficient_history";
  return (
    <div data-testid={`forecast-row-${row.material_id}`} className="rounded-lg border border-[#E2E8F0] dark:border-slate-700 p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-[#0A2540] dark:text-white">{row.name}</span>
            {row.sku && <span className="text-[11px] font-mono text-[#94A3B8]">SKU {row.sku}</span>}
            <Pill tone={STATUS_TONE[row.status]} />
            <Pill tone={CONFIDENCE_TONE[row.confidence]} />
          </div>
          {row.preferred_supplier_name && (
            <div className="text-[11px] text-[#94A3B8] mt-1">
              supplier: {row.preferred_supplier_name} · {row.lead_time_days}d lead
              {row.safety_stock > 0 ? ` · safety ${row.safety_stock}` : ""}
            </div>
          )}
        </div>
        <div className="shrink-0">
          {insufficient ? (
            <span className="text-[11px] text-[#94A3B8] italic">Not enough data</span>
          ) : row.needs_reorder ? (
            <button
              onClick={() => reorder(row)}
              disabled={disabled}
              className="btn-accent text-xs inline-flex items-center gap-1 disabled:opacity-50"
              data-testid={`reorder-btn-${row.material_id}`}
            >
              <Zap className="w-3 h-3" /> Create PO for {row.suggested_qty}
            </button>
          ) : (
            <button
              onClick={() => reorder(row)}
              disabled={disabled}
              className="text-xs py-1.5 px-3 border border-[#E2E8F0] dark:border-slate-700 rounded hover:bg-[#F8FAFC] dark:hover:bg-slate-800 disabled:opacity-30"
              data-testid={`reorder-btn-${row.material_id}`}
            >
              Reorder now
            </button>
          )}
        </div>
      </div>

      <div className={`mt-3 grid gap-3 text-xs ${insufficient ? "grid-cols-2 md:grid-cols-5" : "grid-cols-2 md:grid-cols-7"}`}>
        <Metric label="On hand"     value={row.stock} />
        <Metric label="Reserved"    value={row.reserved} />
        <Metric label="Available"   value={row.available} accent={row.available < 0 ? "red" : ""} />
        <Metric label="Incoming"    value={row.incoming_qty} />
        {!insufficient && (
          <>
            <Metric label="30-day demand" value={row.daily_burn != null ? Math.round(row.daily_burn * 30) : "—"} sub={row.daily_burn != null ? `${row.daily_burn}/day` : ""} />
            <Metric label="Reorder point" value={row.reorder_point ?? "—"} />
            <Metric label="Est. stockout"
              value={row.estimated_stockout || "—"}
              sub={row.days_of_stock != null ? `${row.days_of_stock}d cover` : ""}
              accent={row.status === "stockout" ? "red" : row.status === "order_recommended" ? "amber" : ""}
            />
          </>
        )}
        {insufficient && (
          <Metric label="Consumed 90d" value={row.consumed_90d} sub={`${row.events_90d || 0} events`} />
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, sub, accent }) {
  const tone = accent === "red" ? "text-red-600" : accent === "amber" ? "text-amber-600" : "text-[#0A2540] dark:text-white";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{label}</div>
      <div className={`tabular-nums font-semibold ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#94A3B8]">{sub}</div>}
    </div>
  );
}
